import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import useLanguage from '../hooks/useLanguage';
import { formatProgress } from '../utils/numberFormatter';
import cleaningChecklistApi from '../services/cleaningChecklistApi';
import { supplyContents } from '../config/suppliesLabels';

/**
 * Checklist / supplies sheet for a cleaning task (field crew, mobile-first).
 *
 * One component serves both lists — identical interaction, different states —
 * so the two never drift apart. `mode` selects which.
 *
 * Every tap saves immediately (optimistic, reverted on failure): a cleaner
 * standing in a flat should never hit a submit button or lose answers.
 *
 * Plan: tasks/cleaning-checklist-supplies.md
 */

// Status pills per mode. Colours follow the brand palette:
// green = positive, orange = needs attention, red = functional negative.
const MODES = {
  checklist: {
    titleKey: 'tasks.checklist.title',
    field: 'checklist',
    notePlaceholderKey: 'tasks.checklist.commentPlaceholder',
    noteField: 'comment',
    options: [
      { value: 'done', labelKey: 'tasks.checklist.done', color: '#48b58d' },
      { value: 'not_done', labelKey: 'tasks.checklist.notDone', color: '#DC3545' },
      { value: 'na', labelKey: 'tasks.checklist.na', color: '#6c757d' }
    ]
  },
  supplies: {
    titleKey: 'tasks.checklist.suppliesTitle',
    field: 'supplies',
    notePlaceholderKey: 'tasks.checklist.commentPlaceholder',
    noteField: 'note',
    options: [
      { value: 'ok', labelKey: 'tasks.checklist.available', color: '#48b58d' },
      { value: 'needs_restock', labelKey: 'tasks.checklist.needsRestock', color: '#ff9f53' }
    ]
  }
};

const CleaningChecklistModal = ({ show, onHide, taskId, items = [], mode = 'checklist', onItemsChange }) => {
  const { t, isArabic, language } = useLanguage();
  const config = MODES[mode];

  // Answers live in the parent card, so closing and reopening the sheet never
  // shows stale state and the button counters stay in step without a reload.
  // Saving is tracked PER ROW — a cleaner working down the list on a weak
  // connection must never be blocked by the previous row still in flight.
  const [savingIds, setSavingIds] = useState([]);
  const [error, setError] = useState(null);
  const [openNoteId, setOpenNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  useEffect(() => {
    if (show) {
      setError(null);
      setOpenNoteId(null);
    }
  }, [show]);

  // The freshest rows, for the rollback below. Rows save independently and in parallel, so the
  // `items` array captured when a save STARTED is stale by the time that save fails.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const isSaving = savingIds.length > 0;
  const answered = items.filter(i => i.status).length;
  const total = items.length;

  const save = async (itemId, patch, nextItems, previousItems) => {
    setSavingIds(prev => [...prev, itemId]);
    setError(null);
    onItemsChange(nextItems);  // optimistic — the tap must feel instant
    try {
      const payload = { itemId, ...patch };
      if (mode === 'checklist') {
        await cleaningChecklistApi.updateChecklistItem(taskId, payload);
      } else {
        await cleaningChecklistApi.updateSupplyItem(taskId, payload);
      }
    } catch (err) {
      console.error(`Checklist save failed (${mode}/${itemId}):`, err);
      // 🔴 ROLL BACK ONLY THIS ROW, against the LATEST array — never the whole snapshot taken when
      // this save started. Rows save independently, so restoring the snapshot silently discarded
      // every other answer the cleaner had given while this one was in flight: the counter dropped
      // and they re-answered work the server already held. More likely now that a «لم يتم» item
      // costs two round trips (status, then reason).
      const before = (previousItems || []).find(i => i.itemId === itemId);
      onItemsChange(itemsRef.current.map(i => (i.itemId === itemId && before ? before : i)));
      setError(err?.response?.data?.error || t('tasks.checklist.saveFailed'));
    } finally {
      setSavingIds(prev => prev.filter(id => id !== itemId));
    }
  };

  const handleStatus = (item, value) => {
    if (savingIds.includes(item.itemId)) return;
    // Tapping the active pill clears the answer — mistakes are one tap to undo
    const nextStatus = item.status === value ? null : value;
    // A reason belongs to «لم يتم». Dropping it here mirrors what the server already does, so the
    // optimistic row and the saved row never disagree about whether an explanation still stands.
    const nextItems = items.map(i => (
      i.itemId === item.itemId
        ? { ...i, status: nextStatus, reasonCode: nextStatus === 'not_done' ? i.reasonCode : null }
        : i
    ));
    save(item.itemId, { status: nextStatus }, nextItems, items);
  };

  /**
   * The fixed "why not?" answer (tasks/guest-display-unit-page.md D10).
   *
   * The options travel on the ITEM, snapshotted from the template — nothing here knows which item
   * offers them or what they mean, so no future item needs a code change to use this.
   */
  const handleReason = (item, code) => {
    if (savingIds.includes(item.itemId)) return;
    const next = item.reasonCode === code ? null : code;
    const nextItems = items.map(i => (i.itemId === item.itemId ? { ...i, reasonCode: next } : i));
    save(item.itemId, { reasonCode: next }, nextItems, items);
  };

  const handleNoteSave = (item) => {
    const trimmed = noteDraft.trim();
    setOpenNoteId(null);
    if (trimmed === (item[config.noteField] || '')) return;
    const nextItems = items.map(i => (i.itemId === item.itemId ? { ...i, [config.noteField]: trimmed || null } : i));
    save(item.itemId, { [config.noteField]: trimmed }, nextItems, items);
  };

  // Closing with the keyboard still open would otherwise drop an unsaved note
  const handleHide = () => {
    if (openNoteId) {
      const item = items.find(i => i.itemId === openNoteId);
      if (item) handleNoteSave(item);
    }
    onHide();
  };

  // Checklist rows arrive pre-sorted by section; supplies have none
  const groups = [];
  items.forEach(item => {
    const key = item.section || '';
    const last = groups[groups.length - 1];
    if (last && last.sectionKey === key) last.items.push(item);
    else groups.push({
      sectionKey: key,
      title: isArabic ? item.section : (item.sectionEn || item.section),
      items: [item]
    });
  });

  const renderItem = (item) => {
    const note = item[config.noteField];
    const isNoteOpen = openNoteId === item.itemId;
    // What the item contains — informational. The answer buttons stay on the item itself.
    const contents = supplyContents(t, item.itemId);

    return (
      <div key={item.itemId} className="py-2 border-bottom">
        <div className={`fw-semibold ${contents ? 'mb-1' : 'mb-2'}`} style={{ fontSize: '0.95rem', color: '#090040' }}>
          {isArabic ? item.labelAr : (item.labelEn || item.labelAr)}
        </div>

        {contents && (
          <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>{contents}</div>
        )}

        <div className="d-flex flex-wrap gap-2">
          {config.options.map(option => {
            const active = item.status === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className="touch-btn touch-btn-sm"
                onClick={() => handleStatus(item, option.value)}
                disabled={savingIds.includes(item.itemId)}
                aria-pressed={active}
                style={{
                  flex: '1 1 0',
                  border: `2px solid ${option.color}`,
                  borderRadius: '22px',
                  background: active ? option.color : 'transparent',
                  color: active ? '#fff' : option.color,
                  fontWeight: 600,
                  opacity: savingIds.includes(item.itemId) ? 0.6 : 1
                }}
              >
                {t(option.labelKey)}
              </button>
            );
          })}
        </div>

        {/* The reason row. Appears ONLY for an item that carries options AND is answered «لم يتم» —
            so nothing changes for the other fourteen items, and a cleaner is never asked to explain
            a pass. Deliberately quieter than the status row above it: this is a follow-up to an
            answer already given, not a second decision of equal weight. */}
        {item.status === 'not_done' && (item.reasonOptions || []).length > 0 && (
          <div className="mt-2">
            <div className="text-muted mb-1" style={{ fontSize: '0.78rem' }}>
              {t('tasks.checklist.reasonPrompt')}
            </div>
            <div className="d-flex flex-wrap gap-1">
              {item.reasonOptions.map(option => {
                const active = item.reasonCode === option.code;
                return (
                  <button
                    key={option.code}
                    type="button"
                    className="touch-btn touch-btn-sm"
                    onClick={() => handleReason(item, option.code)}
                    disabled={savingIds.includes(item.itemId)}
                    aria-pressed={active}
                    style={{
                      border: `1px solid ${active ? '#471396' : '#ced4da'}`,
                      borderRadius: '22px',
                      background: active ? '#471396' : 'transparent',
                      color: active ? '#fff' : '#495057',
                      fontSize: '0.82rem',
                      fontWeight: active ? 600 : 400,
                      padding: '0.3rem 0.75rem',
                      opacity: savingIds.includes(item.itemId) ? 0.6 : 1
                    }}
                  >
                    {isArabic ? option.labelAr : (option.labelEn || option.labelAr)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isNoteOpen ? (
          <Form.Control
            as="textarea"
            rows={2}
            autoFocus
            className="mt-2 touch-input"
            value={noteDraft}
            maxLength={500}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={() => handleNoteSave(item)}
            placeholder={t(config.notePlaceholderKey)}
            dir={isArabic ? 'rtl' : 'ltr'}
            style={{ textAlign: isArabic ? 'right' : 'left' }}
          />
        ) : (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 mt-1 text-decoration-none"
            style={{ fontSize: '0.8rem', color: note ? '#471396' : '#6c757d', minHeight: '32px' }}
            onClick={() => { setNoteDraft(note || ''); setOpenNoteId(item.itemId); }}
          >
            {note ? `📝 ${note}` : `＋ ${t('tasks.checklist.addComment')}`}
          </button>
        )}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      fullscreen="sm-down"
      scrollable
      dir={isArabic ? 'rtl' : 'ltr'}
      // While a row is saving, the sheet's own button is the only way out —
      // a stray backdrop tap would hide a failure that reverts the answer
      backdrop={isSaving ? 'static' : true}
      keyboard={!isSaving}
    >
      <Modal.Header closeButton={!isSaving} dir={isArabic ? 'rtl' : 'ltr'}>
        <Modal.Title style={{ fontSize: '1.05rem', color: '#471396' }}>
          {t(config.titleKey)}
          <span className="text-muted" style={{ fontSize: '0.9rem', marginInlineStart: '6px' }}>
            {formatProgress(answered, total, language)}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body dir={isArabic ? 'rtl' : 'ltr'} style={{ textAlign: isArabic ? 'right' : 'left' }}>
        {error && (
          <div
            className="mb-2 p-2 rounded small fw-semibold"
            role="alert"
            style={{ background: '#fdecea', borderInlineStart: '3px solid #DC3545', color: '#090040' }}
          >
            {error}
          </div>
        )}

        {answered < total && (
          <div className="small text-muted mb-2">{t('tasks.checklist.remainingHint')}</div>
        )}
        {total > 0 && answered === total && (
          <div className="small mb-2" style={{ color: '#48b58d' }}>✓ {t('tasks.checklist.allAnswered')}</div>
        )}

        {groups.map((group, idx) => (
          <div key={group.sectionKey || idx} className="mb-2">
            {group.title && (
              <div
                className="fw-bold py-1 mb-1"
                style={{ fontSize: '0.85rem', color: '#471396', borderBottom: '2px solid #f0ecf7' }}
              >
                {group.title}
              </div>
            )}
            {group.items.map(renderItem)}
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer className="touch-modal-actions" dir={isArabic ? 'rtl' : 'ltr'}>
        <Button
          variant="primary"
          className="w-100 fw-bold touch-btn"
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleHide}
          disabled={isSaving}
        >
          {isSaving ? <Spinner animation="border" size="sm" /> : t('tasks.checklist.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CleaningChecklistModal;
